import * as React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Route, Switch, useRouteMatch, Link as RouterLink, useHistory, useLocation, Redirect, Link } from 'react-router-dom';
import { Link as PageLink, Tooltip } from '@mui/material';
import { styled, ThemeProvider } from '@mui/material/styles';
import {
  Typography,
  IconButton,
  Container,
  List,
  Toolbar,
  AppBar as MuiAppBar,
  AppBarProps as MuiAppBarProps,
  Box,
  Drawer as MuiDrawer,
  CssBaseline,
  Alert,
  Grid,
  ListItem,
  Popover,
  Drawer,
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { OnboardingStepStatus, Role } from '@inosio/payments-sdk';
import { clearCredentials, getLoginAsEntity, getUserDetails, selectAuthState, selectCurrentUser } from '../shared/sagas/auth/authSlice';
import Transactions, { useQuery } from './transactions/Transactions';
import Deposits from './deposits/deposits';
import AccountManagement from './accountManagement/accountManagement';
import Properties from './properties/Properties';
import Residents from './residents/Residents';
import PromasIntegration from './integrations/promas_Integration';
import DarkInosio from '../icons/darkinosio.svg';
import ComingSoon from '../shared/ComingSoon';
import { mdTheme, pmUIStyles } from './styles/pmUI';
import { getPmDetails, selectOnboardingApplicationState } from './onboarding/sagas/pmonboardingSlice';
import Dashboard from './dashboard/Dashboard';
// import NotificationIcon from "../icons/NotificationIcon.svg"; temporally
import ProfileWidget from '../shared/login/ProfileWidget';
import PaymentsConfigSettings from './configSettings/paymentsConfigSettings';
import ApplicationsConfigSettings from './configSettings/applicationsConfigSettings';
import PaymentRequests from './paymentrequests/paymentrequests';
import ProfileSettings from '../shared/profileSettings/profileSettings';
import NotificationSettings from '../shared/notificationSettings/notificationSettings';
import BillingConfiguration from './billingConfiguration/BillingConfiguration';
import { useEffect, useState } from 'react';
import Invoicing from './billingConfiguration/Invoicing';
import BankAccounts from './bankAccounts/BankAccounts';
import Vendors from './vendors/Vendors';
import OwnerDistributions from './ownerDistributions/OwnerDistributions';
import Applications from './applications/Applications';
import WorkOrders from './workorders/WorkOrders';
import MaintenanceConfigSettings from './configSettings/maintenanceConfigSettings';
import IntegrationsMarketplace from './integrations/Integrations_marketplace';
import useMediaQuery from '@mui/material/useMediaQuery';
import LogoutIcon from '../icons/LogoutIcon.svg';
import { getAllPropertyGroupsConfigs, getPropertyGroups, selectPropertyGroupDetailsState } from '../shared/sagas/propertyGroups/propertyGroupsSlice';
import PropertyGroups from './configuration/PropertyGroups';
import { LinkBuilder, PmUiLink } from './LinkBuilder';
import ScreeningViewer from './applications/components/ScreeningViewer';
import WorkOrderDetails from './workorders/components/WorkOrderDetails';
import Owners from './owners/Owners';
import { OwnerPortalConfigSettings } from './configSettings/ownerPortalConfigSettings';
import ApplicationDetailsWizard from './applications/components/applicationDetails/ApplicationDetailsWizard';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { getAcknowledgements } from './acknowledgements/sagas/acknowledgementsSlice';
import { resetWorkOrderSearchFilters } from './workorders/sagas/workorderSlice';

// import TenantReportsIntegration from './integrations/tenant_reports_integration'; //temporary until we go live.

// allows me to overide default button variants
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    accountManagement: true;
  }
}

const drawerWidth: number = 100;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DesktopDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  '& .MuiDrawer-paper': {
    position: 'relative',
    whiteSpace: 'nowrap',
    width: drawerWidth,
    backgroundColor: '#053742',
    overflowX: 'hidden',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: 'border-box',
    ...(!open && {
      overflowX: 'hidden',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: theme.spacing(7),
      [theme.breakpoints.up('sm')]: {
        width: theme.spacing(9),
      },
    }),
  },
}));

export function capitalizeFirstLetter(string: any) {
  return string ? string.charAt(0).toUpperCase() + string.slice(1) : null;
}

function PmUIContent() {
  const classes = pmUIStyles();
  const matchesMobile = useMediaQuery('(max-width:768px)');

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  let { path, url } = useRouteMatch();
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(true);
  const currentUser = useSelector(selectCurrentUser);
  const isVendorUser = currentUser?.currentProfile.role === Role.Vendor; //temporary solution
  const selectOnboardingState = useSelector(selectOnboardingApplicationState);
  const { isLogAsSuccess } = useSelector(selectAuthState);
  const query = useQuery();
  const entityuuid = query.get('entityuuid'); //the uuid of the PM we are impersonating
  const [logAsuuid] = useState(entityuuid);
  const enabledFeatures = isVendorUser ? ['Maintenance'] : selectOnboardingState.pmDetails.FeaturesEnabled; //temporary solution until we make the FeaturesEnabled call work for vendors.
  const toggleDrawer = () => setIsDrawerOpen(!isDrawerOpen);
  const linkBuilder = new LinkBuilder(url);
  React.useEffect(() => {
    //close drawer if on mobile
    if (matchesMobile) setIsDrawerOpen(false);
  }, [matchesMobile]);

  React.useEffect(() => {
    if (entityuuid) {
      //check if we need to do logAs
      dispatch(getLoginAsEntity({ ProfileUuid: entityuuid }));
    }
  }, [dispatch, entityuuid]);

  React.useEffect(() => {
    //get Pm & user info as well as property groups
    if (isLogAsSuccess || !logAsuuid) {
      //only invoke when we are ready (if impersonating, that part is done)
      dispatch(getUserDetails());
      //if Vendor user, no need to fetch prop groups for now
      if (isVendorUser) {
        return;
      }
      dispatch(getPmDetails());
      dispatch(getPropertyGroups()); //forgot why we need this at this stage.
    }
  }, [dispatch, isLogAsSuccess, logAsuuid, currentUser]);

  const { shouldRefreshAllPropertyGroupConfigs } = useSelector(selectPropertyGroupDetailsState);

  React.useEffect(() => {
    setPopoverAnchor(null); //! is this needed?
    if (shouldRefreshAllPropertyGroupConfigs && !isVendorUser) dispatch(getAllPropertyGroupsConfigs());
  }, []);//!trying to track down why we need to fetch PG configs on page load.

  React.useEffect(() => {
    if (!currentUser?.accessToken && !(currentUser?.currentProfile && [Role.PropertyManager, Role.PropertyManagerAdmin].includes(currentUser?.currentProfile.role))) {
      //save the desired location the user wanted to go to.
      history.push({
        pathname: '/login',
        state: {
          destination: `${window.location.pathname || ''}${window.location.search || ''}`,
        },
      });
    }

    if (!!currentUser?.accessToken && [Role.MaintenanceCoordinator, Role.MaintenanceStaff].includes(currentUser.currentProfile.role)) {
      if (!window.location.href.includes('configuration/maintenance')) history.push('/pmui/workorders');
    }

    if (!!currentUser?.accessToken && !!isVendorUser) {
      if (!window.location.href.includes('profilesettings')) history.push('/pmui/workorders');
    }
  }, [currentUser, history]);

  //onload fetch any pending acknowledgments
  useEffect(() => {
    dispatch({
      type: getAcknowledgements.type,
      payload: {},
    });
  }, []);

  const [openPopoverText, setOpenPopoverText] = React.useState<string | null>(null);
  const [popoverAnchor, setPopoverAnchor] = React.useState<HTMLButtonElement | null>(null);
  const handleClickOpenPopover = (event: React.MouseEvent<HTMLButtonElement>, text: string) => {
    setPopoverAnchor(event.currentTarget);
    setOpenPopoverText(text);
  };
  const handleClosePopover = () => {
    setPopoverAnchor(null);
    setOpenPopoverText(null);
    setIsDrawerOpen(false);
  };

  const handleResetWorkOrderState = (path: string) => {
    if(path ==='/pmui/workorders'){
      dispatch(resetWorkOrderSearchFilters())
    }
  
  }

  const linksOnLeft = linkBuilder.BuildList(currentUser!, enabledFeatures);
  const renderLinks = (link: any) => (
    <ListItem key={link.Text} selected={link.path === location.pathname}>
      <RouterLink onClick={() => handleResetWorkOrderState(link.path)} to={link.path} id="navLink" className={link.path === location.pathname ? classes.activeListItem : classes.listItem}>
        <IconButton
          sx={{
            backgroundColor: link.path === location.pathname ? '#39A2DB' : '',
            p: 2,
            borderRadius: 5,
          }}
          onClick={() => setIsDrawerOpen(false)}
        >
          <img
            src={link.Icon}
            style={{
              width: link.path === location.pathname ? '35px' : '25px',
              height: link.path === location.pathname ? '29px' : '25px',
            }}
            alt="action"
          />
        </IconButton>
      </RouterLink>
    </ListItem>
  );

  const renderSubLinks = (subMenu: PmUiLink) => (
    <ListItem key={subMenu.Text} selected={subMenu.subLinks?.map((link: PmUiLink) => link.path).includes(location.pathname)}>
      <IconButton
        sx={{
          backgroundColor: subMenu.subLinks?.map((link: PmUiLink) => link.path).includes(location.pathname) ? '#39A2DB' : '',
          p: 2,
          borderRadius: 5,
        }}
        onClick={(e) => handleClickOpenPopover(e, subMenu.Text)}
      >
        <img
          src={subMenu.Icon}
          style={{
            width: false ? '35px' : '25px',
            height: false ? '29px' : '25px',
          }}
          alt="action"
        />
      </IconButton>
      <Popover
        open={openPopoverText === subMenu.Text}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        anchorEl={popoverAnchor}
        onClose={handleClosePopover}
        PaperProps={{
          sx: {
            borderRadius: '0px 24px 24px 0px',
            background: '#053742',
            ml: '22px',
            boxShadow: 'none',
          },
        }}
      >
        <List>
          {subMenu.subLinks?.map((link: PmUiLink) => {
            return (
              <ListItem key={link.Text}>
                <RouterLink to={link.path!} id="navLink" className={link.path === location.pathname ? classes.activeListItem : classes.listItem}>
                  <IconButton sx={{ p: 1, pl: 0 }} onClick={handleClosePopover}>
                    <img src={link.Icon} style={{ width: '25px', height: '25px' }} alt="action" />
                    <Typography
                      sx={{
                        marginLeft: '10px',
                        color: '#fff',
                        fontWeight: 600,
                        fontFamily: 'Work Sans',
                      }}
                    >
                      {link.Text}
                    </Typography>
                  </IconButton>
                </RouterLink>
              </ListItem>
            );
          })}
        </List>
      </Popover>
    </ListItem>
  );

  const selectedLink = () => {
    let returnlink: any;
    linksOnLeft.every((link) => {
      if (link.isSubMenu) {
        const relevantPartOfLink = location.pathname.split('/').slice(0, 3).join('/');
        const llink = link.subLinks?.find((sub) => sub.path === location.pathname || sub.path === relevantPartOfLink);
        if (llink) {
          returnlink = llink;
          return false;
        }
      } else if (link.path === location.pathname) {
        returnlink = link;
        return false;
      } else if (location.pathname.includes('/pmui/workorders/')) {
        returnlink = { Text: 'Work Order' };
        return false;
      }

      return true;
    });
    return returnlink;
  };

  const handleLogout = (event: React.MouseEvent<HTMLElement>) => {
    dispatch(clearCredentials());
    history.push('/login');
  };

  return (
    <ThemeProvider theme={mdTheme}>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        {matchesMobile ? (
          <Drawer
            anchor="left"
            open={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            sx={{
              '& .MuiDrawer-paper': {
                width: drawerWidth,
                backgroundColor: '#053742',
              },
            }}
          >
            <div className={classes.LogoContainer}>
              <IconButton>
                <img src={DarkInosio} style={{ width: '54px', height: '54px' }} alt="action" />
              </IconButton>
              <Typography className={classes.logoName}>{/* {residentsDetails && residentsDetails.Lease.PropertyName.slice(0, 15)}... */}</Typography>
            </div>
            <div className={classes.menuContainer}>
              <List>
                {linksOnLeft.map((link) => (link.isSubMenu ? renderSubLinks(link) : renderLinks(link)))}
                <ListItem>
                  <IconButton
                    sx={{
                      p: 2,
                      borderRadius: 5,
                    }}
                    onClick={handleLogout}
                  >
                    <img
                      src={LogoutIcon}
                      style={{
                        width: '25px',
                        height: '25px',
                      }}
                      alt="action"
                    />
                  </IconButton>
                </ListItem>
              </List>
              <IconButton
                sx={{
                  p: 2,
                  borderRadius: 5,
                }}
              >
                <HelpOutlineIcon style={{ color: 'white' }} />
              </IconButton>
            </div>
          </Drawer>
        ) : (
          <DesktopDrawer variant="permanent" open={true}>
            <div className={classes.LogoContainer}>
              <IconButton>
                <img src={DarkInosio} style={{ width: '54px', height: '54px' }} alt="action" />
              </IconButton>
              <Typography className={classes.logoName}>{/* {residentsDetails && residentsDetails.Lease.PropertyName.slice(0, 15)}... */}</Typography>
            </div>

            <div className={classes.menuContainer}>
              <List>{linksOnLeft.map((link) => (link.isSubMenu ? renderSubLinks(link) : renderLinks(link)))}</List>
              <Box mb={2}>
                <Tooltip title="Need Help ?" placement="top">
                  <IconButton
                    sx={{
                      p: 2,
                      borderRadius: 5,
                    }}
                    onClick={() => {
                      window.open('https://pmsupport.inosio.com/');
                    }}
                  >
                    <HelpOutlineIcon style={{ color: 'white' }} />
                  </IconButton>
                </Tooltip>
                <Typography sx={{ fontFamily: 'Work Sans !important', fontSize: '14px' }} color="white" align="center">
                  {'©'}
                  <PageLink color="inherit" href="https://www.inosio.com/">
                    Inosio
                  </PageLink>
                  <br />
                  {new Date().getFullYear()}
                </Typography>
              </Box>
            </div>
          </DesktopDrawer>
        )}

        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => (theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900]),
            flexGrow: 1,
            height: '100vh',
            overflow: 'auto',
          }}
          style={{ background: '#F1F0F5', border: 'none', boxShadow: 'none' }}
        >
          <AppBar
            position="relative"
            style={{
              background: '#F1F0F5',
              border: 'none',
              boxShadow: 'none',
              display: 'flex',
              alignItems: 'flex-end',
              marginLeft: matchesMobile && isDrawerOpen ? `${drawerWidth}px` : 0,
              width: matchesMobile && isDrawerOpen ? `calc( 100% - ${drawerWidth}px )` : '100%',
            }}
          >
            {selectOnboardingState?.pmDetailsError && selectOnboardingState.pmDetailsError.length > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Alert severity="error">{selectOnboardingState.pmDetailsError}</Alert>
              </Box>
            )}
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2.5, pb: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 1.5, mr: 3 }}>
                {matchesMobile && (
                  <IconButton color="inherit" aria-label="open drawer" onClick={toggleDrawer} edge="start" sx={{ mr: 1, ...(isDrawerOpen && { display: 'none' }) }}>
                    <MenuIcon sx={{ color: '#000', cursor: 'pointer' }}>{'left'}</MenuIcon>
                  </IconButton>
                )}

                <Typography
                  id="mainTitle"
                  component="h1"
                  variant="h6"
                  noWrap
                  sx={{
                    fontFamily: 'Work Sans',
                    color: '#14142B',
                    fontStyle: 'normal',
                    fontWeight: 600,
                    fontSize: '30px',
                  }}
                >
                  {selectedLink()?.Text}
                </Typography>
              </Box>

              {currentUser?.currentProfile.role &&
                [Role.PropertyManagerAdmin, Role.PropertyManager].includes(currentUser?.currentProfile.role) &&
                selectOnboardingState?.pmDetails?.Company &&
                selectOnboardingState?.pmDetails?.Company?.OnboardingStatus === OnboardingStepStatus.Incomplete && (
                  <Link to="/onboarding/wizard" style={{ textDecoration: 'none', display: 'inline-block' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', marginTop: '1%' }}>
                      <Alert severity="warning">{`Please complete onboarding progress - click here to complete it`}</Alert>
                    </Box>
                  </Link>
                )}
              {!matchesMobile && (
                <Toolbar sx={{ position: 'relative', minWidth: '400px', mr: 0.5 }}>
                  <Grid sx={{ position: 'absolute', top: 8, right: 0 }}>
                    <Grid sx={{ display: 'flex' }}>
                      {/* Hide the notification icon temporally */}
                      {/* <IconButton
                        style={{
                          border: "50%",
                          background: "#fff",
                          height: "49px",
                          width: "49px",
                          padding: 2,
                        }}
                      >
                        <img src={NotificationIcon} alt="Notification" />
                      </IconButton> */}
                      <Grid pl={2}>
                        <ProfileWidget />
                      </Grid>
                    </Grid>
                  </Grid>
                </Toolbar>
              )}
            </Box>
          </AppBar>
          <Container maxWidth={false} sx={{ mb: 4 }}>
            <Switch>
              {currentUser?.currentProfile.role && [Role.PropertyManagerAdmin, Role.PropertyManager].includes(currentUser?.currentProfile.role) && (
                <Route path={`${path}/dashboard`}>
                  <Dashboard />
                </Route>
              )}
              <Route exact path={path}>
                <Redirect to={`${path}/dashboard`} />
              </Route>
              <Route path={`${path}/transactions`}>
                <Transactions />
              </Route>
              <Route path={`${path}/deposits`}>
                <Deposits />
              </Route>
              <Route path={`${path}/properties`}>
                <Properties />
              </Route>
              <Route path={`${path}/comingsoon`}>
                <ComingSoon />
              </Route>
              <Route path={`${path}/residents`}>
                <Residents />
              </Route>
              <Route path={`${path}/owners`}>
                <Owners />
              </Route>
              <Route path={`${path}/vendors`}>
                <Vendors />
              </Route>
              <Route path={`${path}/bankaccounts`}>
                <BankAccounts />
              </Route>
              <Route path={`${path}/ownerdistributions`}>
                <OwnerDistributions />
              </Route>
              <Route path={`${path}/paymentrequests`}>
                <PaymentRequests />
              </Route>
              <Route path={`${path}/profilesettings`}>
                <ProfileSettings />
              </Route>
              <Route path={`${path}/emailreports`}>
                <NotificationSettings />
              </Route>
              {enabledFeatures.includes('Maintenance') && (
                <Route path={`${path}/workorders/:id`}>
                  <WorkOrderDetails
                    onBack={() => {
                      history.push('/pmui/workorders');
                    }}
                  />
                </Route>
              )}
              {enabledFeatures.includes('Maintenance') && (
                <Route path={`${path}/workorders`} exact>
                  <WorkOrders />
                </Route>
              )}
              {selectOnboardingState.pmDetails.FeaturesEnabled.includes('Applications') && (
                <Route path={`${path}/applications/:applicationId/screening/:screeningId/`}>
                  <ScreeningViewer />
                </Route>
              )}
              {selectOnboardingState.pmDetails.FeaturesEnabled.includes('Applications') && (
                <Route path={`${path}/applications/:id/`}>
                  <ApplicationDetailsWizard />
                </Route>
              )}
              {selectOnboardingState.pmDetails.FeaturesEnabled.includes('Applications') && (
                <Route path={`${path}/applications`} exact>
                  <Applications />
                </Route>
              )}

              {/* Only admin pms or inosioUsers can access route */}
              {currentUser?.currentProfile.role && [Role.PropertyManagerAdmin].includes(currentUser?.currentProfile.role) ? (
                <Route path={`${path}/accountManagement`}>
                  <AccountManagement />
                </Route>
              ) : null}
              {currentUser?.currentProfile.role && [Role.PropertyManagerAdmin].includes(currentUser?.currentProfile.role) ? (
                <Route path={`${path}/integrations`} exact={true}>
                  <IntegrationsMarketplace pmDetails={selectOnboardingState.pmDetails} />
                </Route>
              ) : null}
              {currentUser?.currentProfile.role && [Role.PropertyManagerAdmin].includes(currentUser?.currentProfile.role) ? (
                <Route path={`${path}/integrations/promas`}>
                  <PromasIntegration pmDetails={selectOnboardingState.pmDetails} />
                </Route>
              ) : null}

              {/* {currentUser?.currentProfile.role && [Role.PropertyManagerAdmin].includes(currentUser?.currentProfile.role)? (
              <Route path={`${path}/integrations/tenantreports`}>
                  <TenantReportsIntegration pmDetails={selectOnboardingState.pmDetails} />
              </Route>
              ) : null} */}
              {currentUser?.currentProfile.role && [Role.PropertyManagerAdmin].includes(currentUser?.currentProfile.role) ? (
                <Route path={`${path}/propertygroups/:uuid/payments`}>
                  <PaymentsConfigSettings />
                </Route>
              ) : null}
              {currentUser?.currentProfile.role && [Role.PropertyManagerAdmin].includes(currentUser?.currentProfile.role) ? (
                <Route path={`${path}/propertygroups/:uuid/applications`}>
                  <ApplicationsConfigSettings />
                </Route>
              ) : null}
              {currentUser?.currentProfile.role && [Role.PropertyManagerAdmin].includes(currentUser?.currentProfile.role) ? (
                <Route path={`${path}/billing`} exact={true}>
                  <BillingConfiguration />
                </Route>
              ) : null}
              {currentUser?.currentProfile.role && [Role.PropertyManagerAdmin, Role.MaintenanceCoordinator, Role.MaintenanceStaff].includes(currentUser?.currentProfile.role) ? (
                <Route path={`${path}/propertygroups/:uuid/maintenance`} exact={true}>
                  <MaintenanceConfigSettings />
                </Route>
              ) : null}
              {currentUser?.currentProfile.role && [Role.PropertyManagerAdmin].includes(currentUser?.currentProfile.role) ? (
                <Route path={`${path}/propertygroups/:uuid/ownerportal`} exact={true}>
                  <OwnerPortalConfigSettings />
                </Route>
              ) : null}
              {currentUser?.currentProfile.role && [Role.PropertyManagerAdmin].includes(currentUser?.currentProfile.role) ? (
                <Route path={`${path}/invoices`}>
                  <Invoicing />
                </Route>
              ) : null}
              {/* Only admin pms or inosioUsers can access route */}
              {currentUser?.currentProfile.role && [Role.PropertyManagerAdmin, Role.MaintenanceCoordinator].includes(currentUser?.currentProfile.role) ? (
                <Route path={`${path}/propertygroups/:uuid?`}>
                  <PropertyGroups />
                </Route>
              ) : null}
            </Switch>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default function PmUI() {
  return <PmUIContent />;
}
